// スプレッドシートからコピーしたデータを記事用JSONに変換するユーティリティ

export interface RankingItem {
  rank: number;
  name: string;
  value: string | number;
  description?: string;
  image?: string;
}

export interface RankingData {
  title: string;
  description: string;
  items: RankingItem[];
  source?: string;
  lastUpdated: string;
}

/**
 * スプレッドシートからコピーしたTSVデータをランキングデータに変換
 * 形式: 順位\t名前\t値\t説明\t画像URL
 */
export const convertSpreadsheetToRanking = (
  tsvData: string,
  title: string,
  description: string,
  source?: string
): RankingData => {
  const lines = tsvData.trim().split('\n');
  const items: RankingItem[] = [];

  for (const line of lines) {
    const columns = line.split('\t');
    if (columns.length >= 3) {
      const item: RankingItem = {
        rank: parseInt(columns[0]) || 0,
        name: columns[1]?.trim() || '',
        value: columns[2]?.trim() || '',
        description: columns[3]?.trim(),
        image: columns[4]?.trim(),
      };
      items.push(item);
    }
  }

  return {
    title,
    description,
    items,
    source,
    lastUpdated: new Date().toISOString().split('T')[0],
  };
};

/**
 * ランキングデータをHTML形式に変換
 */
export const convertRankingToHTML = (rankingData: RankingData): string => {
  let html = `
    <div class="ranking-container bg-white rounded-lg shadow-sm p-6 my-6">
      <h2 class="text-2xl font-bold text-gray-900 mb-4">${rankingData.title}</h2>
      <p class="text-gray-600 mb-6">${rankingData.description}</p>
      
      <div class="ranking-list space-y-4">
  `;

  rankingData.items.forEach((item) => {
    html += `
        <div class="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
          <div class="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
            ${item.rank}
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-gray-900">${item.name}</h3>
            <p class="text-blue-600 font-medium">${item.value}</p>
            ${item.description ? `<p class="text-gray-600 text-sm mt-1">${item.description}</p>` : ''}
          </div>
          ${item.image ? `<img src="${item.image}" alt="${item.name}" class="w-16 h-16 object-cover rounded-lg ml-4">` : ''}
        </div>
    `;
  });

  html += `
      </div>
      ${rankingData.source ? `<p class="text-sm text-gray-500 mt-4">出典: ${rankingData.source}</p>` : ''}
      <p class="text-sm text-gray-500 mt-2">最終更新: ${rankingData.lastUpdated}</p>
    </div>
  `;

  return html;
};

/**
 * ランキングデータをReactコンポーネント用のJSXに変換
 */
export const convertRankingToJSX = (rankingData: RankingData): string => {
  let jsx = `
  <div className="ranking-container bg-white rounded-lg shadow-sm p-6 my-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">${rankingData.title}</h2>
    <p className="text-gray-600 mb-6">${rankingData.description}</p>
    
    <div className="ranking-list space-y-4">
  `;

  rankingData.items.forEach((item) => {
    jsx += `
      <div key="${item.rank}" className="ranking-item flex items-center p-4 bg-gray-50 rounded-lg">
        <div className="rank-badge flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
          ${item.rank}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">${item.name}</h3>
          <p className="text-blue-600 font-medium">${item.value}</p>
          ${item.description ? `<p className="text-gray-600 text-sm mt-1">${item.description}</p>` : ''}
        </div>
        ${item.image ? `<img src="${item.image}" alt="${item.name}" className="w-16 h-16 object-cover rounded-lg ml-4" />` : ''}
      </div>
    `;
  });

  jsx += `
    </div>
    ${rankingData.source ? `<p className="text-sm text-gray-500 mt-4">出典: ${rankingData.source}</p>` : ''}
    <p className="text-sm text-gray-500 mt-2">最終更新: ${rankingData.lastUpdated}</p>
  </div>
  `;

  return jsx;
};

/**
 * 複数のランキングデータをまとめて記事データに変換
 */
export const createArticleFromRankings = (
  rankings: RankingData[],
  articleMeta: {
    id: string;
    title: string;
    description: string;
    youtubeUrl?: string;
    tags: string[];
    category: 'ranking' | 'blog' | 'analysis';
  }
) => {
  const content = rankings.map(ranking => convertRankingToJSX(ranking)).join('\n\n');
  
  return {
    ...articleMeta,
    content,
    publishedAt: new Date().toISOString().split('T')[0],
    updatedAt: new Date().toISOString().split('T')[0],
  };
};
